/**
 * Pagina de edicao dos dados da empresa. Rota
 * /dashboard/configuracoes/empresa/editar. Registro unico (nao ha `:id`),
 * segue o mesmo padrao das demais DetailPages.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, useToast } from "@payflow/ui";
import {
  CompanyForm,
  EMPTY_COMPANY,
  fetchCompany,
  saveCompany,
  type TenantEmpresa,
} from "./SettingsCompanyPage";

export function SettingsCompanyDetailPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState<TenantEmpresa | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchCompany()
      .then((r) => {
        if (!cancelled) setData(r ?? EMPTY_COMPANY);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ variant: "error", title: t("settings.company.load_error") });
          navigate("/dashboard/configuracoes/empresa", { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, t, toast]);

  const handleSave = async (payload: Partial<TenantEmpresa>) => {
    setSaving(true);
    try {
      await saveCompany(payload);
      toast({ variant: "success", title: t("settings.company.saved") });
      navigate("/dashboard/configuracoes/empresa");
    } catch (err) {
      toast({
        variant: "error",
        title:
          err instanceof Error ? err.message : t("settings.company.save_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  const isPJ = data?.tipoPessoa === "pj";
  const title = isPJ
    ? t("settings.company.title_pj")
    : t("settings.company.title_pf");

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard/configuracoes/empresa")}
          className="h-9 w-9 p-0"
          aria-label={t("common.back")}
        >
          <BackIcon />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          {data?.razaoSocial && (
            <p className="text-sm text-muted-foreground">{data.razaoSocial}</p>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col rounded-lg border border-border bg-card px-6 py-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
          </div>
        ) : data ? (
          <CompanyForm
            variant="page"
            initial={data}
            saving={saving}
            onSave={handleSave}
          />
        ) : null}
      </div>
    </div>
  );
}

function BackIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
